/**
 * @typedef {Object} UserMessage
 * @property {string} sender - Always 'user'
 * @property {string} content - The message content
 * @property {string} timestamp - ISO timestamp of when the message was sent
 * @property {number} step - The conversation step number
 * @property {Object|null} selectedChips - The selected chips that were included with this message
 */

/**
 * @typedef {Object} BotMessage
 * @property {string} sender - Always 'bot'
 * @property {string} content - The message content
 * @property {string} timestamp - ISO timestamp of when the message was sent
 * @property {number} step - The conversation step number
 * @property {Object|null} tools - The execution plan for the bot's response
 * @property {string|null} reportVcfId - The VCF report ID if applicable
 */

/**
 * @typedef {Object} MessagePair
 * @property {UserMessage} userMessage
 * @property {BotMessage} botMessage
 * @property {boolean} [isLoading] - Whether the bot message is still loading
 */

/**
 * Creates a new user message
 * @param {string} content - The message content
 * @param {number} step - The conversation step number
 * @param {Object|null} selectedChips - The selected chips data
 * @returns {UserMessage}
 */
export const createUserMessage = (content, step, selectedChips = null) => ({
  sender: 'human',
  content,
  timestamp: new Date().toISOString(),
  selectedChips,
})

/**
 * Creates a new bot message
 * @param {string} content - The message content
 * @param {Object|null} tools - The execution plan for the bot's response
 * @returns {BotMessage}
 */
export const createBotMessage = (content, tools = null) => ({
  sender: 'ai',
  content,
  timestamp: new Date().toISOString(),
  tools,
})

/**
 * Creates a loading message pair
 * @param {UserMessage} userMessage - The user message
 * @returns {MessagePair}
 */
export const createLoadingMessagePair = (userMessage) => ({
  userMessage,
  botMessage: null, // No bot message yet
  isLoading: true,
})

/**
 * Creates a new message pair
 * @param {string} userContent - The user's message content
 * @param {string} botContent - The bot's response content
 * @param {Object|null} tools - The execution plan for the bot's response
 * @param {Object|null} selectedChips - The selected chips data for the user message
 * @returns {MessagePair}
 */
export const createMessagePair = (userContent, botContent, tools = null, selectedChips = null) => ({
  userMessage: createUserMessage(userContent, undefined, selectedChips),
  botMessage: createBotMessage(botContent, tools),
  isLoading: false,
})
